import React, { useMemo, useRef } from 'react';
import { Heart, IconFormIcons } from 'shared/assets/icons';
import { LifeAreaContextMenu } from 'shared/components/connected/life-area-context-menu';
import { useTimeBasedStyle } from 'shared/components/ui/full-calendar-event/use-time-based-style';
import { Icon } from 'shared/components/ui/icon';
import { Tooltip } from 'shared/components/ui/tooltip';
import { useContextMenuState } from 'shared/hooks/use-context-menu-state';
import { ID } from 'shared/types/id';
import { LifeArea } from 'shared/types/life-area';

import * as Styled from './event.style';

export type LifeAreaEventProps = {
  lifeAreaId: ID;
  startDate?: string;
  endDate?: string;
  lifeAreas: LifeArea[];
  onClick?: (id: ID) => void;
};

export const LifeAreaEvent: React.FC<LifeAreaEventProps> = ({
  lifeAreaId,
  startDate,
  endDate,
  lifeAreas,
  onClick,
}) => {
  const lifeArea = useMemo(
    () => lifeAreas.find(({ id }) => id === lifeAreaId),
    [lifeAreas, lifeAreaId],
  );

  const {
    open: openMenu,
    close: closeMenu,
    position: menuPosition,
  } = useContextMenuState({
    disabled: !!lifeArea?.frozenAt,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  const { marginTop, lineHeight, numberOfLines } = useTimeBasedStyle(
    startDate,
    endDate,
  );

  const _onClick = () => lifeArea && onClick?.(lifeArea.id);

  if (!lifeArea || !startDate) {
    return null;
  }

  return (
    <>
      <Styled.Container
        onClick={_onClick}
        onContextMenu={openMenu}
        $color={lifeArea.color}
        $completed={false}
        ref={containerRef}
      >
        <Styled.Content>
          <Styled.IconContainer $color={lifeArea.color ?? undefined}>
            <Icon icon={IconFormIcons[lifeArea.iconName] ?? Heart} />
          </Styled.IconContainer>
          <Styled.Title
            $marginTop={marginTop}
            $lineHeight={lineHeight}
            $completed={false}
            $numberOfLines={numberOfLines}
          >
            {lifeArea.name}
          </Styled.Title>
        </Styled.Content>
      </Styled.Container>

      {!!menuPosition && (
        <LifeAreaContextMenu
          lifeArea={lifeArea}
          onClose={closeMenu}
          location={menuPosition}
        />
      )}
      <Tooltip
        referenceElement={containerRef}
        label={lifeArea.name}
        delay={400}
      />
    </>
  );
};
